import { roundN } from "~/utils/functions/roundN";

// // // //

/**
 * Formats a currency value in thousands ("K"), millions ("M") or billions ("B").
 *
 * Takes a numerical value and returns a string representing the value
 * in thousands, millions, or billions of dollars, rounded to one decimal place.
 *
 * - Values below 1 million are formatted in thousands (e.g., $100,000 becomes "100 K").
 * - Values below 1 billion are formatted in millions (e.g., $1,500,000 becomes "1.5 M").
 * - Values of 1 billion or more are formatted in billions (e.g., $1,500,000,000 becomes "1.5 B").
 *
 * @param {number} value - The currency value to format.
 * @param {number} [smallValueDecimalPlaces=2] - Decimal places for values below one thousand.
 * @returns {string} The formatted currency string.
 * @example
 * formatCurrencyValue(100000) // "100 K"
 * formatCurrencyValue(1500000) // "1.5 M"
 * formatCurrencyValue(1500000000) // "1.5 B"
 */
export function formatCurrencyValueCompact(
    value: number,
    smallValueDecimalPlaces: number = 2,
): string {
    // Positive or negative?
    const absValue = Math.abs(value);
    const isNegative = absValue !== value;
    const normalizedDecimalPlaces = Math.max(
        0,
        Math.floor(smallValueDecimalPlaces),
    );

    // Handle positive/negative signage
    const signage = isNegative ? "-" : "";

    // Hard zero
    if (absValue === 0) return (0).toFixed(normalizedDecimalPlaces);

    const threshold = 0.0001;

    // 1️⃣ Near-zero threshold
    if (absValue < threshold) {
        return `${signage}< ${threshold}`;
    }

    // 2️⃣ Very small values (avoid forcing 2 decimals)
    if (absValue < 0.01) {
        return `${signage}${Number(absValue.toPrecision(4)).toString()}`;
    }

    // Handle billion or greater
    if (absValue >= 1_000_000_000) {
        return `${signage}${roundN(absValue / 1_000_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (absValue >= 1_000_000) {
        return `${signage}${roundN(absValue / 1_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}M`;
    }
    // Handle thousand or greater but less than a million
    else if (absValue >= 1_000) {
        return `${signage}${roundN(absValue / 1_000, 1).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}K`;
    }

    // Display without any formatting if value is less than 1000
    return `${signage}${absValue.toLocaleString("en-US", {
        maximumFractionDigits: normalizedDecimalPlaces,
        minimumFractionDigits: normalizedDecimalPlaces,
    })}`;
}
